/**
 * EL EMBUDO — el tablero arrastrable.
 *
 * ── QUE RESUELVE ────────────────────────────────────────────────────────────
 *
 * La lista de Leads dice quien hay; no dice en que va cada uno ni cuanto hay
 * en juego. Esto pone las mismas personas en columnas, con su valor y su fecha
 * esperada de cierre, y se mueven arrastrando.
 *
 * ── ARRASTRAR SIN LIBRERIA ──────────────────────────────────────────────────
 *
 * Con el arrastre nativo del navegador (`draggable` + `onDragOver` + `onDrop`).
 * No hay ninguna libreria de drag-and-drop en el proyecto y no hace falta
 * añadir una para mover una tarjeta entre seis columnas — una dependencia
 * nueva se confirma antes de meterla, no se asume.
 *
 * ── LA TARJETA SE MUEVE ANTES DE QUE EL SERVIDOR CONTESTE ───────────────────
 *
 * Y si el servidor dice que no, se vuelve sola a su sitio y se explica por que.
 * Esperar la respuesta para pintar el movimiento hace que arrastrar se sienta
 * roto en una conexion lenta; moverla y no volverla nunca seria mentir.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Plus, GripVertical, Phone, Mail, Calendar, DollarSign, Loader, AlertTriangle } from 'lucide-react';
import {
  getPipelineBoard, moveLeadToStage, setLeadDeal,
  createPipelineStage, updatePipelineStage, deletePipelineStage,
} from '../api.js';

const C = {
  borde: 'var(--c-border,#1a3550)',
  fondo: 'var(--c-surface,#0c1c2e)',
  fondo2: 'var(--c-surface2,#112538)',
  texto: 'var(--c-text,#c8dff0)',
  texto2: 'var(--c-text2,#7aa3c0)',
  texto3: 'var(--c-text3,#45688a)',
  cyan: 'var(--c-cyan,#00d4ff)',
};

const ESTADOS = ['NEW', 'CONTACTED', 'QUALIFIED', 'PROPOSAL', 'WON', 'LOST'];

/** Dinero, en en-US y sin decimales: en un tablero los centavos son ruido. */
function plata(n) {
  if (!n) return '';
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
}

/**
 * "12 sep" a partir de "YYYY-MM-DD", SIN pasar por UTC.
 *
 * `new Date('2026-09-12')` se interpreta como medianoche UTC y en Florida se
 * pinta como el 11. Por eso se parten las partes a mano: es el mismo fallo que
 * esta casa ya arreglo en la fecha de seguimiento.
 */
function dia(iso, lang) {
  if (!iso) return '';
  const [a, m, d] = String(iso).slice(0, 10).split('-').map(Number);
  if (!a || !m || !d) return '';
  return new Date(a, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/** ¿La fecha de seguimiento ya pasó? Con el día LOCAL, no el de UTC. */
function yaVencio(iso) {
  if (!iso) return false;
  const hoy = new Date();
  const local = `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, '0')}-${String(hoy.getDate()).padStart(2, '0')}`;
  return String(iso).slice(0, 10) < local;
}

export function PipelineView({ lang = 'es', puedeEditar = true, refreshSignal = 0 }) {
  const es = lang === 'es';
  const [tablero, setTablero] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');
  const [aviso, setAviso] = useState('');
  // La tarjeta que se arrastra va en un REF, no solo en estado.
  //
  // `onDragStart` y `onDrop` pueden ocurrir en el mismo tick sin que React
  // haya vuelto a pintar entre medias, y entonces el manejador de `drop`
  // todavia lee el estado VIEJO —null— y la tarjeta no se mueve. Con una
  // persona arrastrando hay cientos de milisegundos de por medio y casi
  // siempre funciona; "casi siempre" es justo el fallo que aparece en el
  // telefono de alguien y no se puede reproducir. El ref se lee al instante.
  // (Se encontro arrastrando de verdad en un navegador, no leyendo el codigo.)
  const arrastrandoRef = useRef(null);
  const [arrastrando, setArrastrando] = useState(null);   // solo para atenuar la tarjeta
  const [encima, setEncima] = useState(null);             // stageId sobre el que se suelta
  const [editando, setEditando] = useState(null);         // lead cuyo trato se edita
  const soltando = useRef(false);

  const cargar = useCallback(async (pipelineId) => {
    setError('');
    try {
      setTablero(await getPipelineBoard(pipelineId));
    } catch (err) {
      // El frontend se publica unos minutos antes que el backend: entre medias
      // esta pantalla pide una ruta que todavia no existe. Un 404 no puede
      // dejarla rota.
      setError(es ? 'No se pudo cargar el embudo.' : 'Could not load the pipeline.');
      if (typeof console !== 'undefined') console.warn('getPipelineBoard', err?.message);
    } finally {
      setCargando(false);
    }
  }, [es]);

  useEffect(() => { cargar(); }, [cargar, refreshSignal]);

  // ── Arrastrar ────────────────────────────────────────────────────────────
  async function soltarEn(stageId) {
    setEncima(null);
    const info = arrastrandoRef.current;
    arrastrandoRef.current = null;
    setArrastrando(null);
    if (!info || soltando.current) return;
    if (info.desdeStageId === stageId) return;   // no se movio de columna

    // Se mueve YA en pantalla, y se guarda el estado anterior por si hay que
    // devolverla.
    const antes = tablero;
    setTablero((t) => {
      if (!t) return t;
      const stages = t.stages.map((col) => {
        if (col.id === info.desdeStageId) {
          const quedan = col.leads.filter((l) => l.id !== info.leadId);
          return { ...col, leads: quedan, total: Math.max(0, col.total - 1) };
        }
        if (col.id === stageId) {
          const lead = t.stages.find((x) => x.id === info.desdeStageId)?.leads.find((l) => l.id === info.leadId);
          return lead ? { ...col, leads: [lead, ...col.leads], total: col.total + 1 } : col;
        }
        return col;
      });
      return { ...t, stages };
    });

    soltando.current = true;
    try {
      await moveLeadToStage(info.leadId, stageId);
      // Se vuelve a pedir el tablero: los totales y las sumas los calcula el
      // servidor, y adivinarlos aqui es como se separan de la verdad.
      await cargar(tablero?.pipeline?.id);
    } catch (err) {
      setTablero(antes);
      setAviso(es ? 'No se pudo mover la tarjeta. Vuelve a intentarlo.' : 'Could not move the card. Try again.');
      if (typeof console !== 'undefined') console.warn('moveLeadToStage', err?.message);
    } finally {
      soltando.current = false;
    }
  }

  // ── Columnas ─────────────────────────────────────────────────────────────
  async function anadirColumna() {
    if (!tablero?.pipeline?.id) return;
    const nombre = window.prompt(es ? 'Nombre de la columna nueva:' : 'Name of the new column:');
    if (!nombre || !nombre.trim()) return;
    try {
      await createPipelineStage(tablero.pipeline.id, {
        name: nombre.trim(),
        // Una columna nueva nace representando el estado que ya tiene la que
        // va antes: asi no se inventa un significado que el informe no sabria
        // contar. Se puede cambiar despues.
        mappedStatus: tablero.stages[tablero.stages.length - 1]?.mappedStatus || 'NEW',
        position: tablero.stages.length,
      });
      await cargar(tablero.pipeline.id);
    } catch (err) {
      setAviso(err?.message || (es ? 'No se pudo crear la columna.' : 'Could not create the column.'));
    }
  }

  async function renombrarColumna(col) {
    const nombre = window.prompt(es ? 'Nuevo nombre:' : 'New name:', col.name);
    if (!nombre || !nombre.trim() || nombre === col.name) return;
    try {
      await updatePipelineStage(col.id, { name: nombre.trim() });
      await cargar(tablero?.pipeline?.id);
    } catch (err) {
      setAviso(err?.message || (es ? 'No se pudo renombrar.' : 'Could not rename.'));
    }
  }

  async function quitarColumna(col) {
    // Se dice CUANTAS tarjetas hay dentro antes de preguntar. Borrar una
    // columna con 30 leads sin saberlo es la clase de sorpresa que hace que
    // nadie vuelva a tocar el tablero.
    const cuantas = col.total;
    const texto = es
      ? `Quitar la columna "${col.name}"?\n\n${cuantas > 0
          ? `Tiene ${cuantas} ${cuantas === 1 ? 'tarjeta' : 'tarjetas'}. NO se borran: vuelven a la columna que les toque por su estado.`
          : 'Esta vacia.'}`
      : `Remove the "${col.name}" column?\n\n${cuantas > 0
          ? `It has ${cuantas} ${cuantas === 1 ? 'card' : 'cards'}. They are NOT deleted: they go back to the column their status maps to.`
          : 'It is empty.'}`;
    if (!window.confirm(texto)) return;
    const r = await deletePipelineStage(col.id);
    if (!r.ok) {
      setAviso(r.error || (es ? 'No se pudo quitar la columna.' : 'Could not remove the column.'));
      return;
    }
    await cargar(tablero?.pipeline?.id);
  }

  // ── Pintar ───────────────────────────────────────────────────────────────
  if (cargando) {
    return <div style={{ padding: '2rem', textAlign: 'center', color: C.texto3 }}><Loader size={18} className="dkt-spin" /></div>;
  }

  if (error) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', color: C.texto2, fontSize: '.8rem' }}>
        {error}
        <div style={{ marginTop: '.6rem' }}>
          <button type="button" className="dkt-btn xs" onClick={() => cargar()}>{es ? 'Reintentar' : 'Retry'}</button>
        </div>
      </div>
    );
  }

  if (!tablero) {
    return (
      <div style={{ padding: '2.5rem 1rem', textAlign: 'center', color: C.texto3 }}>
        <div style={{ fontSize: '.85rem', fontWeight: 700, color: C.texto2 }}>
          {es ? 'Todavia no tienes un embudo' : 'You have no pipeline yet'}
        </div>
        <div style={{ fontSize: '.72rem', marginTop: '.4rem', lineHeight: 1.6, maxWidth: '26rem', margin: '.4rem auto 0' }}>
          {es
            ? 'Se crea uno con las seis etapas de siempre y todos tus leads aparecen colocados solos.'
            : 'One gets created with the usual six stages and all your leads show up already placed.'}
        </div>
      </div>
    );
  }

  const totalEnJuego = tablero.stages.reduce((s, col) => s + (col.valueSum || 0), 0);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '.6rem', marginBottom: '.6rem', flexWrap: 'wrap' }}>
        <div style={{ fontSize: '.85rem', fontWeight: 700 }}>{tablero.pipeline.name}</div>
        {totalEnJuego > 0 && (
          <div style={{ fontSize: '.72rem', color: C.cyan, fontWeight: 700 }}>
            {plata(totalEnJuego)} {es ? 'en juego' : 'in play'}
          </div>
        )}
        <div style={{ flex: 1 }} />
        {puedeEditar && (
          <button type="button" className="dkt-btn xs" onClick={anadirColumna}>
            <Plus size={12} />{es ? 'Columna' : 'Column'}
          </button>
        )}
      </div>

      {aviso && (
        <div style={{
          marginBottom: '.6rem', fontSize: '.72rem', color: '#ffb700',
          background: 'rgba(255,183,0,.1)', border: '1px solid rgba(255,183,0,.3)',
          borderRadius: 8, padding: '.4rem .55rem', display: 'flex', alignItems: 'center', gap: '.4rem',
        }}>
          <AlertTriangle size={13} />{aviso}
          <button type="button" onClick={() => setAviso('')} style={{ marginLeft: 'auto', background: 'transparent', border: 'none', color: 'inherit', cursor: 'pointer' }}>×</button>
        </div>
      )}

      <div className="dkt-embudo">
        {tablero.stages.map((col) => (
          <div
            key={col.id}
            className="dkt-embudo-col"
            onDragOver={(e) => { e.preventDefault(); setEncima(col.id); }}
            onDragLeave={() => setEncima((x) => (x === col.id ? null : x))}
            onDrop={(e) => { e.preventDefault(); soltarEn(col.id); }}
            style={{
              border: `1px solid ${encima === col.id ? C.cyan : C.borde}`,
              background: encima === col.id ? 'rgba(0,212,255,.06)' : C.fondo,
              borderRadius: 12,
            }}
          >
            <div style={{ padding: '.5rem .6rem', borderBottom: `1px solid ${C.borde}` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '.35rem' }}>
                <span style={{ width: 8, height: 8, borderRadius: 99, background: col.color || C.texto3, flexShrink: 0 }} />
                <span style={{ fontSize: '.75rem', fontWeight: 700, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {col.name}
                </span>
                <span style={{ fontSize: '.68rem', color: C.texto3, fontWeight: 700 }}>{col.total}</span>
              </div>
              {col.valueSum > 0 && (
                <div style={{ fontSize: '.65rem', color: C.texto2, marginTop: '.15rem' }}>{plata(col.valueSum)}</div>
              )}
              {puedeEditar && (
                <div style={{ display: 'flex', gap: '.25rem', marginTop: '.3rem' }}>
                  <button type="button" className="dkt-btn xs" onClick={() => renombrarColumna(col)}>
                    {es ? 'Renombrar' : 'Rename'}
                  </button>
                  <button type="button" className="dkt-btn xs" onClick={() => quitarColumna(col)}>
                    {es ? 'Quitar' : 'Remove'}
                  </button>
                </div>
              )}
            </div>

            <div style={{ padding: '.4rem', display: 'flex', flexDirection: 'column', gap: '.4rem', minHeight: '4rem' }}>
              {col.leads.length === 0 && (
                <div style={{ fontSize: '.66rem', color: C.texto3, textAlign: 'center', padding: '.8rem .2rem' }}>
                  {es ? 'Vacia' : 'Empty'}
                </div>
              )}

              {col.leads.map((l) => (
                <div
                  key={l.id}
                  draggable={puedeEditar}
                  onDragStart={() => {
                    arrastrandoRef.current = { leadId: l.id, desdeStageId: col.id };
                    setArrastrando({ leadId: l.id, desdeStageId: col.id });
                  }}
                  onDragEnd={() => { arrastrandoRef.current = null; setArrastrando(null); setEncima(null); }}
                  style={{
                    background: C.fondo2, border: `1px solid ${C.borde}`, borderRadius: 9,
                    padding: '.45rem .5rem', cursor: puedeEditar ? 'grab' : 'default',
                    opacity: arrastrando?.leadId === l.id ? .45 : 1,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '.3rem' }}>
                    {puedeEditar && <GripVertical size={11} color={C.texto3} style={{ flexShrink: 0 }} />}
                    <span style={{ fontSize: '.74rem', fontWeight: 700, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {`${l.firstName || ''} ${l.lastName || ''}`.trim() || (es ? 'Sin nombre' : 'No name')}
                    </span>
                  </div>

                  {(l.phone || l.email) && (
                    <div style={{ fontSize: '.63rem', color: C.texto3, marginTop: '.2rem', display: 'flex', alignItems: 'center', gap: '.3rem', overflow: 'hidden' }}>
                      {l.phone ? <Phone size={9} /> : <Mail size={9} />}
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.phone || l.email}</span>
                    </div>
                  )}

                  <div style={{ display: 'flex', alignItems: 'center', gap: '.4rem', marginTop: '.3rem', flexWrap: 'wrap' }}>
                    {l.value ? (
                      <span style={{ fontSize: '.65rem', fontWeight: 700, color: '#22c55e' }}>{plata(l.value)}</span>
                    ) : null}
                    {l.followUpDate && (
                      <span style={{
                        fontSize: '.62rem', display: 'inline-flex', alignItems: 'center', gap: '.2rem',
                        color: yaVencio(l.followUpDate) ? '#ef4444' : C.texto3,
                        fontWeight: yaVencio(l.followUpDate) ? 700 : 400,
                      }}>
                        <Calendar size={9} />{dia(l.followUpDate, lang)}
                      </span>
                    )}
                    {puedeEditar && (
                      <button
                        type="button"
                        onClick={() => setEditando(l)}
                        style={{ marginLeft: 'auto', background: 'transparent', border: 'none', color: C.texto3, cursor: 'pointer', padding: 0, fontSize: '.62rem', display: 'inline-flex', alignItems: 'center', gap: '.15rem' }}
                      >
                        <DollarSign size={9} />{es ? 'Valor' : 'Value'}
                      </button>
                    )}
                  </div>
                </div>
              ))}

              {col.hasMore && (
                <div style={{ fontSize: '.64rem', color: C.texto3, textAlign: 'center', padding: '.3rem' }}>
                  {es
                    ? `+${col.total - col.leads.length} mas — usa la lista de Leads para verlos todos`
                    : `+${col.total - col.leads.length} more — use the Leads list to see them all`}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {editando && (
        <EditorDeTrato
          lead={editando}
          lang={lang}
          onCerrar={() => setEditando(null)}
          onGuardado={async () => { setEditando(null); await cargar(tablero?.pipeline?.id); }}
          onError={(m) => setAviso(m)}
        />
      )}
    </div>
  );
}

/** El valor y la fecha esperada de cierre de una tarjeta. */
function EditorDeTrato({ lead, lang, onCerrar, onGuardado, onError }) {
  const es = lang === 'es';
  const [valor, setValor] = useState(lead.value ?? '');
  const [fecha, setFecha] = useState(lead.expectedCloseAt ? String(lead.expectedCloseAt).slice(0, 10) : '');
  const [guardando, setGuardando] = useState(false);

  async function guardar() {
    setGuardando(true);
    try {
      await setLeadDeal(lead.id, {
        value: valor === '' ? null : Number(valor),
        expectedCloseAt: fecha || null,
      });
      await onGuardado();
    } catch (err) {
      onError(err?.message || (es ? 'No se pudo guardar.' : 'Could not save.'));
      setGuardando(false);
    }
  }

  return (
    <div
      onClick={onCerrar}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 60, padding: '1rem' }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ background: C.fondo, border: `1px solid ${C.borde}`, borderRadius: 12, padding: '.9rem', width: '20rem', maxWidth: '100%' }}
      >
        <div style={{ fontSize: '.8rem', fontWeight: 700, marginBottom: '.6rem' }}>
          {`${lead.firstName || ''} ${lead.lastName || ''}`.trim()}
        </div>

        <label style={{ fontSize: '.68rem', color: C.texto2, display: 'block', marginBottom: '.2rem' }}>
          {es ? 'Cuanto vale si se cierra' : 'What it is worth if it closes'}
        </label>
        <input
          type="number" min="0" step="1" value={valor}
          onChange={(e) => setValor(e.target.value)}
          placeholder="0"
          style={{ width: '100%', background: C.fondo2, border: `1px solid ${C.borde}`, borderRadius: 8, padding: '.4rem .5rem', color: C.texto, fontSize: '.75rem', fontFamily: 'inherit', marginBottom: '.6rem' }}
        />

        <label style={{ fontSize: '.68rem', color: C.texto2, display: 'block', marginBottom: '.2rem' }}>
          {es ? 'Cuando esperas cerrarlo' : 'When you expect to close it'}
        </label>
        <input
          type="date" value={fecha}
          onChange={(e) => setFecha(e.target.value)}
          style={{ width: '100%', background: C.fondo2, border: `1px solid ${C.borde}`, borderRadius: 8, padding: '.4rem .5rem', color: C.texto, fontSize: '.75rem', fontFamily: 'inherit', marginBottom: '.8rem' }}
        />

        <div style={{ display: 'flex', gap: '.4rem', justifyContent: 'flex-end' }}>
          <button type="button" className="dkt-btn xs" onClick={onCerrar}>{es ? 'Cancelar' : 'Cancel'}</button>
          <button type="button" className="dkt-btn primary xs" onClick={guardar} disabled={guardando}>
            {guardando ? <Loader size={12} className="dkt-spin" /> : null}{es ? 'Guardar' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default PipelineView;
